'use client';

import { useEffect, useRef, useState } from 'react';
import type { Game } from '@/lib/games';
import { COVER_FRAMES } from '@/lib/gameCovers';
import { LOADER_THEMES, type LoaderTheme } from '@/lib/gameLoaderThemes';

/**
 * The loading screen over a game frame (owner, 2026-10-11): a big game takes a
 * moment to download and boot, and a blank frame in that moment reads as
 * broken. Iron Talon gets its own house style — steel, hazard stripes, the
 * gold title, a striped progress bar and field tips; Brickfront gets a kiln-lit
 * wall that is laid brick by brick as the bar fills. Every other game is
 * themed too, from a description rather than by hand: lib/gameLoaderThemes.ts
 * says what each one looks like and what its progress IS (discs dropping, tiles
 * doubling, a car on the coast road …) and `ThemedLoader` below draws it. A
 * game with no entry there falls back to a plain bar.
 *
 * The bar is staged, not byte-exact: it eases towards a ceiling per phase
 * (`fetch` = the page downloading, `boot` = loaded and starting up) and only
 * fills when the game says it has painted (`done` — Iron Talon posts
 * `gt:game-ready`; other games finish on the frame's load event). Brickfront
 * joins READY_SIGNAL in GamesMenu once games/tanks.html posts the event; until
 * then it finishes on load like the rest.
 */

export type LoadPhase = 'fetch' | 'boot' | 'done';

const CEIL: Record<LoadPhase, number> = { fetch: 72, boot: 94, done: 100 };

const TALON_TIPS = [
  'A flawless clear — no deaths — pays +25% KHARMA.',
  'Press Tab mid-fight to open the shop and buy lives.',
  'Esc pauses the mission and opens the menu.',
  'The first clear of every mission pays a bonus on top.',
  'Play online co-op — clearing together builds your bond.',
  'Win a versus match and take the pot.',
];

/** Brickfront's foreman's notes: each one is something the player can use. */
const BRICK_TIPS = [
  'Your own shells break brick too — mind what stands between you and the Core.',
  'Press Tab mid-siege to open the shop: reserve tanks, shells, a mason.',
  'A mason re-lays the wall around the Core in steel.',
  'A flawless clear — no tank lost — pays +25% KHARMA.',
  'The first clear of every mission pays a bonus on top.',
  'Esc pauses the siege and opens the menu.',
  'Hold the wall together in online co-op, or bring down a rival Core in a duel and take the pot.',
];

/** Brickfront's wall: 3 courses of 12, laid left to right from the bottom course up. */
const BRICK_COURSES = 3;
const BRICK_PER_COURSE = 12;
const BRICK_TOTAL = BRICK_COURSES * BRICK_PER_COURSE;

function brickStage(pct: number): string {
  if (pct >= 100) return 'The wall stands';
  if (pct < 25) return 'Hauling clay from the pits';
  if (pct < 50) return 'Firing the kilns';
  if (pct < 72) return 'Laying the courses';
  return 'Crews to their tanks';
}

function stageOf(pct: number, talon: boolean): string {
  if (pct >= 100) return talon ? 'Ready for deployment' : 'Ready';
  if (!talon) return 'Loading';
  if (pct < 25) return 'Downloading mission data';
  if (pct < 50) return 'Loading the arsenal';
  if (pct < 72) return 'Calibrating targeting';
  return 'Deploying the squad';
}

export default function GameLoader({ game, phase, onGone }: { game: Game; phase: LoadPhase; onGone: () => void }) {
  const talon = game.id === 'contra';
  const brick = game.id === 'tanks';
  const [pct, setPct] = useState(0);
  const [fade, setFade] = useState(false);
  const [tip, setTip] = useState(0);
  const pctRef = useRef(0);
  const goneRef = useRef(onGone);
  goneRef.current = onGone;

  // Ease towards this phase's ceiling; at `done`, fill, hold a beat, fade, go.
  useEffect(() => {
    const ceil = CEIL[phase];
    const t = setInterval(() => {
      const cur = pctRef.current;
      const next = phase === 'done' ? Math.min(100, cur + Math.max(4, (100 - cur) * 0.35)) : cur + (ceil - cur) * 0.045;
      pctRef.current = next;
      setPct(next);
    }, 50);
    return () => clearInterval(t);
  }, [phase]);
  useEffect(() => {
    if (phase !== 'done' || pct < 100 || fade) return;
    const a = setTimeout(() => setFade(true), 260);
    const b = setTimeout(() => goneRef.current(), 260 + 380);
    return () => { clearTimeout(a); clearTimeout(b); };
  }, [phase, pct, fade]);
  const tips = talon ? TALON_TIPS : brick ? BRICK_TIPS : null;
  useEffect(() => {
    if (!tips) return;
    setTip(Math.floor(Math.random() * tips.length));
    const t = setInterval(() => setTip((i) => (i + 1) % tips.length), 2800);
    return () => clearInterval(t);
  }, [tips]);

  const shown = Math.floor(pct);
  const cover = COVER_FRAMES[game.id] ? `/games/covers/${game.id}-1.webp` : null;

  if (brick) {
    // Bricks laid so far; the wall is only complete at a true 100.
    const laid = pct >= 100 ? BRICK_TOTAL : Math.min(BRICK_TOTAL - 1, Math.floor((pct / 100) * BRICK_TOTAL));
    return (
      <div role="status" aria-live="polite" aria-label={`Loading ${game.name}: ${shown}%`}
           className={'gt-bfl pointer-events-none absolute inset-0 z-20 overflow-hidden transition-opacity duration-300 ' + (fade ? 'opacity-0' : 'opacity-100')}>
        <style>{BRICK_CSS}</style>
        {cover && (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={cover} alt="" aria-hidden className="gt-bfl-bg" />
        )}
        <div className="gt-bfl-veil" aria-hidden />
        <div className="gt-bfl-glow" aria-hidden />
        <div className="gt-bfl-embers" aria-hidden><i /><i /><i /><i /><i /><i /></div>

        <div className="gt-bfl-center">
          <p className="gt-bfl-kick">TrueMarch Arcade · The brickworks</p>
          <h2 className="gt-bfl-title">{game.name}</h2>
          <div className="gt-bfl-wall" aria-hidden>
            {Array.from({ length: BRICK_COURSES }, (_, c) => (
              <div key={c} className={'gt-bfl-course' + (c % 2 ? ' off' : '')}>
                {Array.from({ length: BRICK_PER_COURSE }, (_, b) => {
                  const n = c * BRICK_PER_COURSE + b;
                  return <i key={b} className={n < laid ? 'laid' : n === laid ? 'next' : ''} />;
                })}
              </div>
            ))}
          </div>
          <div className="gt-bfl-meta">
            <span>{brickStage(pct)}<b className="gt-bfl-dots"><em>.</em><em>.</em><em>.</em></b></span>
            <span className="gt-bfl-pct">{String(shown).padStart(2, '0')}%</span>
          </div>
          <p key={tip} className="gt-bfl-tip"><span>Foreman</span>{BRICK_TIPS[tip % BRICK_TIPS.length]}</p>
        </div>
      </div>
    );
  }

  const theme = LOADER_THEMES[game.id];
  if (!talon && theme) {
    return <ThemedLoader game={game} theme={theme} pct={pct} shown={shown} fade={fade} cover={cover} />;
  }

  if (!talon) {
    return (
      <div role="status" aria-live="polite" aria-label={`Loading ${game.name}: ${shown}%`}
           className={'pointer-events-none absolute inset-0 z-20 grid place-items-center bg-ink-950 transition-opacity duration-300 ' + (fade ? 'opacity-0' : 'opacity-100')}>
        {cover && (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={cover} alt="" aria-hidden className="absolute inset-0 h-full w-full scale-105 object-cover opacity-25 blur-sm" />
        )}
        <div className="relative flex w-[min(320px,80%)] flex-col items-center gap-3 text-white">
          <p className="text-lg font-semibold tracking-tight">{game.name}</p>
          <div className="h-1 w-full overflow-hidden rounded-full bg-white/15">
            <div className="h-full rounded-full bg-white transition-[width] duration-100" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-xs tabular-nums text-white/60">{stageOf(pct, false)} · {shown}%</p>
        </div>
      </div>
    );
  }

  return (
    <div role="status" aria-live="polite" aria-label={`Loading Iron Talon: ${shown}%`}
         className={'gt-itl pointer-events-none absolute inset-0 z-20 overflow-hidden transition-opacity duration-300 ' + (fade ? 'opacity-0' : 'opacity-100')}>
      <style>{TALON_CSS}</style>
      {cover && (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={cover} alt="" aria-hidden className="gt-itl-bg" />
      )}
      <div className="gt-itl-veil" aria-hidden />
      <div className="gt-itl-scan" aria-hidden />
      <div className="gt-itl-hazard top" aria-hidden />
      <div className="gt-itl-hazard bottom" aria-hidden />

      <div className="gt-itl-center">
        <p className="gt-itl-kick">TrueMarch Arcade · Mission briefing</p>
        <h2 className="gt-itl-title">Iron Talon</h2>
        <div className="gt-itl-bar" aria-hidden>
          <i style={{ width: `${pct}%` }} />
        </div>
        <div className="gt-itl-meta">
          <span>{stageOf(pct, true)}<b className="gt-itl-dots"><em>.</em><em>.</em><em>.</em></b></span>
          <span className="gt-itl-pct">{String(shown).padStart(2, '0')}%</span>
        </div>
        <p key={tip} className="gt-itl-tip"><span>Field tip</span>{TALON_TIPS[tip]}</p>
      </div>
    </div>
  );
}

/** Cells set down so far; the last one only lands at a true 100. */
function laidOf(pct: number, n: number): number {
  return pct >= 100 ? n : Math.min(n - 1, Math.floor((pct / 100) * n));
}

/**
 * The loader every described game shares (lib/gameLoaderThemes.ts): one
 * layout, and the theme decides the ground, the type, the weather and — the
 * part that matters — what the progress is made of.
 */
function ThemedLoader({ game, theme, pct, shown, fade, cover }: {
  game: Game; theme: LoaderTheme; pct: number; shown: number; fade: boolean; cover: string | null;
}) {
  const p = theme.progress;
  const stage = pct >= 100 ? theme.ready : theme.stages[pct < 25 ? 0 : pct < 50 ? 1 : pct < 72 ? 2 : 3];
  const fx = theme.fx ?? [];
  const vars = {
    '--bg': theme.bg, '--ink': theme.ink, '--t': theme.title, '--a': theme.a, '--b': theme.b,
  } as React.CSSProperties;

  let progress: React.ReactNode;
  if (p.kind === 'bar') {
    progress = <div className={'gt-gl-bar ' + p.style} aria-hidden><i style={{ width: `${pct}%` }} /></div>;
  } else if (p.kind === 'track') {
    progress = (
      <div className={'gt-gl-track h-' + p.head + (p.road ? ' road' : '')} aria-hidden>
        <b style={{ width: `${pct}%` }} />
        <u style={{ left: `${pct}%` }} />
      </div>
    );
  } else {
    const rows = p.rows ?? 1;
    const per = Math.ceil(p.n / rows);
    const laid = laidOf(pct, p.n);
    progress = (
      <div className={'gt-gl-cells s-' + p.shape + (p.anim ? ' a-' + p.anim : '')} aria-hidden>
        {Array.from({ length: rows }, (_, r) => (
          <div key={r} className="gt-gl-row">
            {Array.from({ length: Math.min(per, p.n - r * per) }, (_, c) => {
              const n = r * per + c;
              const on = n < laid;
              const style = {
                '--c': p.hues ? p.hues[n % p.hues.length] : undefined,
                '--y': p.lift ? `${p.lift[n % p.lift.length]}px` : undefined,
                height: p.tall ? p.tall[n % p.tall.length] : undefined,
              } as React.CSSProperties;
              return (
                <i key={c} className={on ? 'laid' : n === laid ? 'next' : ''} style={style}>
                  {on && p.glyphs ? p.glyphs[n % p.glyphs.length] : null}
                </i>
              );
            })}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div role="status" aria-live="polite" aria-label={`Loading ${game.name}: ${shown}%`} style={vars}
         className={'gt-gl f-' + theme.font + (theme.light ? ' light' : '') +
                    ' pointer-events-none absolute inset-0 z-20 overflow-hidden transition-opacity duration-300 ' +
                    (fade ? 'opacity-0' : 'opacity-100')}>
      <style>{THEMED_CSS}</style>
      {cover && (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={cover} alt="" aria-hidden className="gt-gl-bg" />
      )}
      <div className="gt-gl-veil" aria-hidden />
      {fx.length > 0 && (
        <div className={'gt-gl-fx ' + fx.map((f) => 'x-' + f).join(' ')} aria-hidden>
          <i /><i /><i /><i /><i /><i /><i /><i />
        </div>
      )}

      <div className="gt-gl-center">
        <p className="gt-gl-kick">TrueMarch Arcade · {theme.kick}</p>
        <h2 className="gt-gl-title">{game.name}</h2>
        {progress}
        <div className="gt-gl-meta">
          <span>{stage}<b className="gt-gl-dots"><em>.</em><em>.</em><em>.</em></b></span>
          <span className="gt-gl-pct">{String(shown).padStart(2, '0')}%</span>
        </div>
        {/* The game's own blurb — the one line here that states facts. */}
        <p className="gt-gl-note"><span>{theme.tag}</span>{game.blurb}</p>
      </div>
    </div>
  );
}

/* The shared themed loader, scoped under .gt-gl. Colours come in as custom properties
   (--bg ground, --ink text, --t title, --a / --b the two accents); the classes pick the
   type (f-*), the progress (bar / track h-* / cells s-* a-*) and the weather (x-*).
   Nothing that must be seen starts from opacity 0: a title or a laid cell is there
   whether or not its animation runs. */
const THEMED_CSS = `
.gt-gl { --well: rgba(255,255,255,.09); --line: rgba(255,255,255,.2); --w: min(440px, 82%);
  background: var(--bg); color: var(--ink); font-family: "Segoe UI", system-ui, Arial, sans-serif; }
.gt-gl.light { --well: rgba(0,0,0,.08); --line: rgba(0,0,0,.22); }
.gt-gl-bg { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; transform: scale(1.06);
  filter: blur(4px) saturate(.9) brightness(.34); animation: gt-gl-settle 15s ease-out both; }
.gt-gl.light .gt-gl-bg { filter: blur(5px) saturate(.85); opacity: .2; }
.gt-gl-veil { position: absolute; inset: 0; background: radial-gradient(ellipse at 50% 42%, transparent 0%, var(--bg) 78%); }
.gt-gl-center { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 14px; padding: 24px; text-align: center; }
/* Nothing in the column gives way: an empty bar is the first thing a short frame would squash flat. */
.gt-gl-center > * { flex-shrink: 0; }
.gt-gl-kick { margin: 0; font-size: 11px; font-weight: 800; letter-spacing: .34em; text-transform: uppercase; color: var(--a); }
.gt-gl.light .gt-gl-kick { color: var(--ink); opacity: .6; }
.gt-gl-title { margin: 0 0 6px; font-size: clamp(26px, min(6.6vw, 12vh), 68px); line-height: 1; color: var(--t);
  text-shadow: 0 0 26px color-mix(in srgb, var(--a) 45%, transparent), 3px 3px 0 color-mix(in srgb, var(--bg) 55%, #000);
  animation: gt-gl-in .5s ease-out both; }
.gt-gl.light .gt-gl-title { text-shadow: 2px 3px 0 color-mix(in srgb, var(--a) 55%, transparent); }
.gt-gl.f-block .gt-gl-title { font-family: "Arial Black", Impact, sans-serif; font-weight: 900; text-transform: uppercase; letter-spacing: .05em; }
.gt-gl.f-race .gt-gl-title { font-family: "Arial Black", Impact, sans-serif; font-weight: 900; font-style: italic; text-transform: uppercase; letter-spacing: .03em; }
.gt-gl.f-slab .gt-gl-title { font-family: Rockwell, "Roboto Slab", Georgia, serif; font-weight: 900; text-transform: uppercase; letter-spacing: .07em; }
.gt-gl.f-serif .gt-gl-title { font-family: Georgia, "Times New Roman", serif; font-weight: 700; letter-spacing: .02em; }
.gt-gl.f-mono .gt-gl-title { font-family: Consolas, "Courier New", ui-monospace, monospace; font-weight: 400; font-size: clamp(20px, min(4vw, 8vh), 38px); text-shadow: none; }
.gt-gl.f-round .gt-gl-title { font-family: "Trebuchet MS", "Segoe UI", Verdana, sans-serif; font-weight: 800; }
.gt-gl.f-neon .gt-gl-title { font-weight: 300; text-transform: uppercase; letter-spacing: .26em; margin-right: -.26em;
  font-size: clamp(22px, min(5.2vw, 10vh), 54px); text-shadow: 0 0 6px var(--t), 0 0 22px var(--a), 0 0 46px var(--a); }
.gt-gl.f-hand .gt-gl-title { font-family: "Comic Sans MS", "Segoe Print", "Bradley Hand", cursive; font-weight: 700; rotate: -2deg; }
.gt-gl.f-mono, .gt-gl.f-mono .gt-gl-meta { font-family: Consolas, "Courier New", ui-monospace, monospace; }
.gt-gl.f-hand .gt-gl-meta, .gt-gl.f-hand .gt-gl-note { font-family: "Comic Sans MS", "Segoe Print", "Bradley Hand", cursive; }
.gt-gl.f-serif .gt-gl-note { font-family: Georgia, "Times New Roman", serif; font-style: italic; }
.gt-gl-meta { display: flex; justify-content: space-between; gap: 16px; width: var(--w);
  font-weight: 700; font-size: 12px; letter-spacing: .12em; text-transform: uppercase; color: var(--ink); }
.gt-gl-meta > span:first-child { opacity: .72; }
.gt-gl-pct { color: var(--t); font-weight: 900; font-variant-numeric: tabular-nums; }
.gt-gl-dots em { font-style: normal; animation: gt-gl-blink 1.2s infinite; } .gt-gl-dots em:nth-child(2) { animation-delay: .2s; } .gt-gl-dots em:nth-child(3) { animation-delay: .4s; }
.gt-gl-note { margin: 16px 0 0; max-width: min(540px, 92%); font-size: 13px; line-height: 1.5; color: var(--ink); opacity: .9; }
.gt-gl-note span { margin-right: 10px; padding: 3px 8px; font-size: 10px; font-weight: 900; font-style: normal; letter-spacing: .18em;
  text-transform: uppercase; color: var(--bg); background: var(--a); border-radius: 2px; }
.gt-gl.light .gt-gl-note span { color: #fff; background: var(--ink); }

/* ── bars ── */
.gt-gl-bar { position: relative; width: var(--w); height: 12px; background: var(--well); }
.gt-gl-bar i { position: absolute; left: 0; top: 0; bottom: 0; transition: width .1s linear; background: linear-gradient(90deg, var(--a), var(--b)); }
.gt-gl-bar.stripe { height: 14px; border: 2px solid var(--line);
  clip-path: polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px); }
.gt-gl-bar.stripe i { background: repeating-linear-gradient(135deg, rgba(255,255,255,.2) 0 8px, transparent 8px 16px), linear-gradient(90deg, var(--a), var(--b));
  background-size: 22px 100%, 100% 100%; animation: gt-gl-crawl .7s linear infinite; }
.gt-gl-bar.pill { height: 16px; border-radius: 999px; overflow: hidden; box-shadow: inset 0 0 0 2px var(--line); }
.gt-gl-bar.pill i { border-radius: 999px; box-shadow: inset 0 3px 0 rgba(255,255,255,.35), inset 0 -3px 0 rgba(0,0,0,.18); }
.gt-gl-bar.beam { height: 3px; }
.gt-gl-bar.beam i { box-shadow: 0 0 12px var(--a); }
.gt-gl-bar.beam i::after { content: ""; position: absolute; right: -4px; top: 50%; width: 8px; height: 8px; margin-top: -4px; border-radius: 50%;
  background: #fff; box-shadow: 0 0 12px 3px var(--b); }
.gt-gl-bar.scribble { height: 18px; background: transparent; border: 2px solid var(--ink); border-radius: 9px 14px 8px 13px / 12px 8px 14px 9px; overflow: hidden; }
.gt-gl-bar.scribble i { background: repeating-linear-gradient(62deg, var(--a) 0 3px, transparent 3px 6px), repeating-linear-gradient(-58deg, var(--b) 0 2px, transparent 2px 9px); }

/* ── tracks: a trail (b) and a head (u) ── */
.gt-gl-track { position: relative; width: var(--w); height: 28px; }
.gt-gl-track::before { content: ""; position: absolute; left: 0; right: 0; top: 50%; height: 4px; margin-top: -2px; border-radius: 2px; background: var(--well); }
.gt-gl-track.road::before { height: 16px; margin-top: -8px; border-radius: 3px; background: color-mix(in srgb, var(--ink) 22%, transparent); }
.gt-gl-track.road::after { content: ""; position: absolute; left: 0; right: 0; top: 50%; height: 2px; margin-top: -1px;
  background: repeating-linear-gradient(90deg, var(--line) 0 10px, transparent 10px 22px); }
.gt-gl-track b { position: absolute; left: 0; top: 50%; height: 4px; margin-top: -2px; border-radius: 2px; transition: width .1s linear;
  background: linear-gradient(90deg, var(--a), var(--b)); box-shadow: 0 0 10px var(--a); }
.gt-gl-track.road b { opacity: .55; }
.gt-gl-track u { position: absolute; top: 50%; z-index: 1; translate: -50% -50%; transition: left .1s linear; text-decoration: none; }
.gt-gl-track.h-chevron u { width: 22px; height: 15px; background: var(--t); clip-path: polygon(0 0, 62% 0, 100% 50%, 62% 100%, 0 100%, 28% 50%); }
.gt-gl-track.h-moon u { width: 20px; height: 20px; border-radius: 50%; background: var(--t); box-shadow: inset -5px -2px 0 rgba(0,0,0,.22), 0 0 18px var(--b); }
.gt-gl-track.h-ship u { width: 24px; height: 18px; background: var(--t); clip-path: polygon(0 0, 100% 50%, 0 100%, 24% 50%); filter: drop-shadow(0 0 6px var(--a)); }
.gt-gl-track.h-box u { width: 14px; height: 14px; rotate: 8deg; background: var(--a); border: 2px solid var(--b); box-shadow: 0 0 14px var(--a); }
.gt-gl-track.h-car u { width: 30px; height: 12px; margin-top: -3px; border-radius: 7px 10px 3px 3px; background: var(--b);
  box-shadow: inset 9px 3px 0 -3px rgba(255,255,255,.6); }
.gt-gl-track.h-car u::after { content: ""; position: absolute; left: 4px; right: 4px; bottom: -4px; height: 7px;
  background: radial-gradient(circle at 4px 50%, var(--ink) 3.5px, transparent 4px), radial-gradient(circle at calc(100% - 4px) 50%, var(--ink) 3.5px, transparent 4px); }
.gt-gl-track.h-bird u { width: 18px; height: 16px; border-radius: 55% 45% 50% 50%; background: var(--a);
  box-shadow: inset -4px -3px 0 rgba(0,0,0,.12); animation: gt-gl-bob .5s ease-in-out infinite alternate; }
.gt-gl-track.h-bird u::after { content: ""; position: absolute; left: 100%; top: 5px; border: 4px solid transparent; border-left: 7px solid var(--b); }
.gt-gl-track.h-bird u::before { content: ""; position: absolute; right: 4px; top: 4px; width: 3px; height: 3px; border-radius: 50%; background: var(--ink); }
.gt-gl-track.h-coil b { height: 12px; margin-top: -6px; border-radius: 6px; }
.gt-gl-track.h-coil u { width: 18px; height: 18px; border-radius: 50%; background: var(--b); box-shadow: 0 0 14px var(--a); }
.gt-gl-track.h-coil u::before { content: ""; position: absolute; right: 3px; top: 4px; width: 4px; height: 4px; border-radius: 50%; background: var(--bg);
  box-shadow: 0 6px 0 var(--bg); }
.gt-gl-track.h-ball { border-left: 5px solid var(--a); border-right: 5px solid var(--b); border-radius: 3px; }
.gt-gl-track.h-ball::before { height: 2px; margin-top: 8px; }
.gt-gl-track.h-ball b { display: none; }
.gt-gl-track.h-ball u { width: 11px; height: 11px; border-radius: 50%; background: #fff; box-shadow: 0 0 10px var(--a); animation: gt-gl-bounce .42s ease-in infinite alternate; }
.gt-gl-track.h-link { background: radial-gradient(circle at 7px 50%, var(--a) 6px, transparent 7px), radial-gradient(circle at calc(100% - 7px) 50%, var(--b) 6px, transparent 7px); }
.gt-gl-track.h-link::before { left: 7px; right: 7px; }
.gt-gl-track.h-link b { left: 7px; height: 6px; margin-top: -3px; max-width: calc(100% - 14px); }
.gt-gl-track.h-link u { width: 10px; height: 10px; border-radius: 50%; background: #fff; box-shadow: 0 0 12px 2px var(--b); }

/* ── cells: things set down one at a time ── */
.gt-gl-cells { display: flex; flex-direction: column-reverse; align-items: center; gap: 5px; width: var(--w); }
.gt-gl-row { position: relative; display: flex; justify-content: center; align-items: flex-end; gap: 5px; width: 100%; }
.gt-gl-row i { position: relative; flex: 0 1 var(--cw, 24px); min-width: 0; height: var(--ch, 14px); display: grid; place-items: center;
  font: 800 11px/1 "Segoe UI", Arial, sans-serif; font-style: normal; color: var(--bg); border-radius: 3px;
  background: var(--well); box-shadow: inset 0 0 0 1px var(--line); transform: translateY(var(--y, 0px)); }
.gt-gl-row i.laid { background: var(--c, var(--a)); box-shadow: inset 0 1px 0 rgba(255,255,255,.4), inset 0 -2px 0 rgba(0,0,0,.25); }
.gt-gl-row i.next { animation: gt-gl-blink 1s ease-in-out infinite; background: var(--b); box-shadow: none; }
.gt-gl-cells.a-drop i.laid { animation: gt-gl-drop .26s ease-out both; }
.gt-gl-cells.a-pop i.laid { animation: gt-gl-pop .24s ease-out both; }
.gt-gl-cells.a-flip i.laid { animation: gt-gl-flip .3s ease-out both; }
.gt-gl-cells.a-slide i.laid { animation: gt-gl-slide .24s ease-out both; }
.gt-gl-cells.a-rise i.laid { animation: gt-gl-rise .26s ease-out both; }
.s-sq { --cw: 20px; --ch: 20px; }
.s-tile { --cw: 34px; --ch: 30px; } .s-tile i { border-radius: 5px; font-size: 11px; color: #3a2f25; letter-spacing: -.02em; }
.s-round, .s-coin, .s-rev, .s-fruit, .s-pad { --cw: 24px; --ch: 24px; }
.s-round i, .s-coin i, .s-rev i, .s-fruit i, .s-pad i { border-radius: 50%; flex-shrink: 0; }
.s-round .gt-gl-row { padding: 6px 10px; border-radius: 8px; background: color-mix(in srgb, #2b56c8 70%, var(--bg)); width: auto; }
.s-coin i.laid { box-shadow: inset 0 0 0 3px rgba(255,255,255,.45), inset 0 0 0 5px rgba(0,0,0,.14), 0 2px 0 rgba(0,0,0,.2); }
.s-rev { --cw: 16px; --ch: 16px; } .s-rev i.laid { box-shadow: 0 0 12px var(--c); }
.s-fruit i.laid::after { content: ""; position: absolute; inset: -2px; background: linear-gradient(118deg, transparent 45%, var(--bg) 45% 55%, transparent 55%); }
.s-pad i.laid { background: conic-gradient(from 25deg, transparent 0 32deg, var(--c, var(--a)) 32deg); box-shadow: none; }
.s-diamond { --cw: 24px; --ch: 24px; } .s-diamond i { clip-path: polygon(50% 0, 100% 50%, 50% 100%, 0 50%); border-radius: 0; }
.s-diamond i.laid { background: linear-gradient(135deg, #fff -40%, var(--c) 45%, color-mix(in srgb, var(--c) 60%, #000)); }
.s-shard { --cw: 18px; --ch: 30px; } .s-shard i { clip-path: polygon(50% 0, 100% 34%, 72% 100%, 28% 100%, 0 34%); border-radius: 0; }
.s-shard i.laid { background: linear-gradient(100deg, #fff -30%, var(--c) 50%, color-mix(in srgb, var(--c) 55%, #000)); }
.s-hex { --cw: 26px; --ch: 23px; } .s-hex i { clip-path: polygon(25% 0, 75% 0, 100% 50%, 75% 100%, 25% 100%, 0 50%); border-radius: 0; }
.s-card { --cw: 32px; --ch: 44px; } .s-card i { border-radius: 4px; font: 700 12px/1 Georgia, serif; }
.s-card i.laid { background: #fdf9ef; color: var(--c); box-shadow: 0 2px 6px rgba(0,0,0,.4), inset 0 0 0 1px rgba(0,0,0,.12); }
.s-shell { --cw: 11px; --ch: 22px; } .s-shell i { border-radius: 6px 6px 2px 2px; }
.s-shell i.laid { background: linear-gradient(180deg, var(--c) 0 38%, #c9b26a 38% 44%, #8f7a3c 44%); }
.s-flame { --cw: 18px; --ch: 26px; } .s-flame i { border-radius: 50% 50% 50% 50% / 68% 68% 32% 32%; }
.s-flame i.laid { background: radial-gradient(ellipse at 50% 78%, #fff6c8 0 18%, var(--c) 46%, color-mix(in srgb, var(--c) 40%, #a01800) 100%);
  box-shadow: 0 0 16px var(--c); animation: gt-gl-rise .26s ease-out both, gt-gl-flicker 1.1s ease-in-out infinite alternate; }
.s-balloon { --cw: 24px; --ch: 30px; } .s-balloon .gt-gl-row { margin-bottom: 10px; }
.s-balloon i { border-radius: 50% 50% 50% 50% / 56% 56% 44% 44%; }
.s-balloon i.laid { box-shadow: inset -4px -5px 0 rgba(0,0,0,.14), inset 4px 4px 0 rgba(255,255,255,.3); }
.s-balloon i::after { content: ""; position: absolute; left: 50%; top: 100%; width: 1px; height: 10px; background: var(--line); }
.s-eq { --cw: 12px; } .s-eq .gt-gl-row { height: 36px; gap: 4px; } .s-eq i { border-radius: 2px 2px 0 0; }
.s-stair { --cw: 30px; --ch: 8px; } .s-stair .gt-gl-row { gap: 2px; align-items: flex-start; height: 40px; } .s-stair i { border-radius: 1px; }
.s-tower { --cw: 22px; --ch: 30px; } .s-tower i { border-radius: 0;
  clip-path: polygon(0 100%, 0 0, 22% 0, 22% 18%, 39% 18%, 39% 0, 61% 0, 61% 18%, 78% 18%, 78% 0, 100% 0, 100% 100%); }
.s-tower i.laid { background: linear-gradient(180deg, var(--c), color-mix(in srgb, var(--c) 60%, #000)); }
.s-window { --cw: 16px; --ch: 20px; } .s-window .gt-gl-row { gap: 4px; } .s-window i { border-radius: 2px; }
.s-window i.laid { box-shadow: 0 0 10px var(--c); }
.s-hole { --cw: 34px; --ch: 13px; } .s-hole .gt-gl-row { padding-top: 20px; gap: 8px; }
.s-hole i, .s-hole i.laid, .s-hole i.next { border-radius: 50%; background: var(--ink); box-shadow: none; animation: none; }
.s-hole i.laid::after { content: ""; position: absolute; left: 22%; bottom: 40%; width: 56%; height: 19px; border-radius: 50% 50% 30% 30%;
  background: var(--c); box-shadow: inset 0 -5px 0 rgba(0,0,0,.14); animation: gt-gl-rise .24s ease-out both; }
.s-hole i.laid::before { content: ""; position: absolute; z-index: 1; left: 50%; bottom: 150%; width: 5px; height: 4px; margin-left: -2.5px; border-radius: 50%; background: var(--b); }
.s-note { --cw: 17px; --ch: 13px; } .s-note .gt-gl-row { height: 46px; gap: 12px; padding-top: 22px; align-items: flex-end;
  background: repeating-linear-gradient(0deg, transparent 0 5px, var(--line) 5px 6px) 0 100% / 100% 30px no-repeat; }
.s-note i { border-radius: 50%; background: transparent; box-shadow: none; transform: translateY(var(--y, 0px)) rotate(-18deg); }
.s-note i.laid { box-shadow: 0 0 10px var(--c); }
.s-note i.laid::after { content: ""; position: absolute; right: 0; bottom: 50%; width: 2px; height: 22px; background: var(--c); rotate: 18deg; transform-origin: bottom; }
.s-pellet { --cw: 8px; --ch: 8px; } .s-pellet .gt-gl-row { align-items: center; height: 24px; gap: 9px; }
.s-pellet i { border-radius: 50%; background: var(--a); box-shadow: 0 0 6px var(--a); flex-shrink: 0; }
.s-pellet i.laid { visibility: hidden; }
.s-pellet i.next { flex-basis: 22px; height: 22px; opacity: 1; box-shadow: 0 0 12px var(--b);
  background: conic-gradient(from 60deg, transparent 0 60deg, var(--b) 60deg); animation: gt-gl-chomp .28s linear infinite alternate; }
.s-slash { --cw: 20px; --ch: 22px; } .s-slash .gt-gl-row { gap: 4px; }
.s-slash i { border-radius: 2px; transform: skewX(-20deg); }
.s-lantern { --cw: 22px; --ch: 28px; } .s-lantern i { border-radius: 42% 42% 46% 46% / 30% 30% 34% 34%; }
.s-lantern i.laid { background: radial-gradient(ellipse at 50% 60%, #fff8dc 0 14%, var(--c) 52%, color-mix(in srgb, var(--c) 55%, #401000));
  box-shadow: 0 0 16px var(--c), inset 0 3px 0 rgba(0,0,0,.25), inset 0 -3px 0 rgba(0,0,0,.25); }
.s-glass { --cw: 36px; --ch: 13px; } .s-glass .gt-gl-row { gap: 4px; }
.s-glass i.laid { background: linear-gradient(180deg, color-mix(in srgb, var(--c) 55%, #fff), var(--c) 60%); box-shadow: 0 0 10px color-mix(in srgb, var(--c) 60%, transparent); opacity: .92; }
.s-crate { --cw: 26px; --ch: 26px; } .s-crate i { border-radius: 2px; }
.s-crate i.laid { border: 3px solid rgba(0,0,0,.32); box-shadow: none;
  background: linear-gradient(45deg, transparent 44%, rgba(0,0,0,.32) 44% 56%, transparent 56%), linear-gradient(-45deg, transparent 44%, rgba(0,0,0,.32) 44% 56%, transparent 56%), var(--c); }
.s-git { --cw: 14px; --ch: 14px; } .s-git .gt-gl-row { gap: 0; justify-content: space-between; height: 22px; align-items: center; }
.s-git .gt-gl-row::before { content: ""; position: absolute; left: 0; right: 0; top: 50%; height: 2px; margin-top: -1px; background: var(--line); }
.s-git i { border-radius: 50%; flex-shrink: 0; background: var(--bg); box-shadow: inset 0 0 0 2px var(--line); }
.s-git i.laid { background: var(--bg); box-shadow: inset 0 0 0 3px var(--c); }

/* ── weather ── */
.gt-gl-fx { position: absolute; inset: 0; overflow: hidden; }
.gt-gl-fx i { position: absolute; display: none; }
.gt-gl-fx i:nth-child(1) { left: 8%; top: 14%; animation-delay: -.4s; } .gt-gl-fx i:nth-child(2) { left: 21%; top: 66%; animation-delay: -2.9s; }
.gt-gl-fx i:nth-child(3) { left: 34%; top: 28%; animation-delay: -1.6s; } .gt-gl-fx i:nth-child(4) { left: 47%; top: 82%; animation-delay: -4.1s; }
.gt-gl-fx i:nth-child(5) { left: 59%; top: 10%; animation-delay: -3.3s; } .gt-gl-fx i:nth-child(6) { left: 71%; top: 58%; animation-delay: -1.1s; }
.gt-gl-fx i:nth-child(7) { left: 83%; top: 22%; animation-delay: -5.2s; } .gt-gl-fx i:nth-child(8) { left: 93%; top: 74%; animation-delay: -2.2s; }
.gt-gl-fx.x-embers i { display: block; top: auto; bottom: -6px; width: 3px; height: 3px; border-radius: 50%; background: var(--b);
  box-shadow: 0 0 6px 1px var(--a); animation: gt-gl-float 6s linear infinite; }
.gt-gl-fx.x-bubbles i { display: block; top: auto; bottom: -16px; width: 12px; height: 12px; border-radius: 50%; border: 1.5px solid var(--b); opacity: .55;
  animation: gt-gl-float 7.5s linear infinite; }
.gt-gl-fx.x-bubbles i:nth-child(odd) { width: 18px; height: 18px; border-color: var(--a); }
.gt-gl-fx.x-rain i { display: block; top: -44px; width: 1.5px; height: 38px; background: linear-gradient(transparent, var(--b)); opacity: .5; rotate: 12deg;
  animation: gt-gl-fall 1s linear infinite; }
.gt-gl-fx.x-rain i:nth-child(odd) { animation-duration: 1.35s; }
.gt-gl-fx.x-petals i { display: block; top: -12px; width: 9px; height: 7px; border-radius: 70% 0 70% 0; background: var(--a); opacity: .75;
  animation: gt-gl-sway 8s linear infinite; }
.gt-gl-fx.x-petals i:nth-child(odd) { animation-duration: 10.5s; }
.gt-gl-fx.x-stars { background:
  radial-gradient(1px 1px at 12% 22%, #fff, transparent), radial-gradient(1px 1px at 27% 71%, #fff, transparent),
  radial-gradient(1.5px 1.5px at 41% 12%, #fff, transparent), radial-gradient(1px 1px at 55% 86%, #fff, transparent),
  radial-gradient(1px 1px at 66% 31%, #fff, transparent), radial-gradient(1.5px 1.5px at 78% 64%, #fff, transparent),
  radial-gradient(1px 1px at 89% 17%, #fff, transparent), radial-gradient(1px 1px at 96% 48%, #fff, transparent),
  radial-gradient(1px 1px at 4% 55%, #fff, transparent), radial-gradient(1px 1px at 48% 44%, #fff, transparent); opacity: .8; }
.gt-gl-fx.x-stars i { display: block; width: 3px; height: 3px; border-radius: 50%; background: #fff; box-shadow: 0 0 8px 1px var(--a);
  animation: gt-gl-blink 2.6s ease-in-out infinite; }
.gt-gl-fx.x-dust i { display: block; width: 3px; height: 3px; border-radius: 50%; background: var(--ink); opacity: .28; animation: gt-gl-drift 11s linear infinite; }
.gt-gl-fx.x-scan::before { content: ""; position: absolute; inset: 0; opacity: .16;
  background: repeating-linear-gradient(0deg, rgba(255,255,255,.08) 0 1px, transparent 1px 3px); }
.gt-gl-fx.x-grid::after { content: ""; position: absolute; left: -30%; right: -30%; bottom: 0; height: 46%; opacity: .26;
  background: linear-gradient(var(--a) 1px, transparent 1px) 0 0 / 100% 24px, linear-gradient(90deg, var(--a) 1px, transparent 1px) 0 0 / 48px 100%;
  transform: perspective(280px) rotateX(64deg); transform-origin: 50% 100%; animation: gt-gl-grid 1.1s linear infinite;
  -webkit-mask-image: linear-gradient(transparent, #000 70%); mask-image: linear-gradient(transparent, #000 70%); }
.gt-gl-fx.x-tunnel::before { content: ""; position: absolute; inset: -30%; opacity: .16;
  background: repeating-radial-gradient(circle at 50% 46%, transparent 0 46px, var(--a) 46px 48px, transparent 48px 92px, var(--b) 92px 94px);
  animation: gt-gl-tunnel 2.2s linear infinite; }
.gt-gl-fx.x-paper::before { content: ""; position: absolute; inset: 0; opacity: .5;
  background: repeating-linear-gradient(0deg, transparent 0 27px, color-mix(in srgb, var(--ink) 16%, transparent) 27px 28px),
    linear-gradient(90deg, transparent 0 9%, color-mix(in srgb, var(--a) 45%, transparent) 9% calc(9% + 1px), transparent calc(9% + 1px)); }
.gt-gl-fx.x-sun::after { content: ""; position: absolute; left: 0; right: 0; bottom: 0; height: 48%;
  background: radial-gradient(ellipse at 50% 118%, color-mix(in srgb, var(--b) 55%, transparent), color-mix(in srgb, var(--a) 16%, transparent) 46%, transparent 72%);
  animation: gt-gl-breathe 3.6s ease-in-out infinite; }

@keyframes gt-gl-settle { to { transform: scale(1); } }
@keyframes gt-gl-in { from { transform: translateY(10px) scale(.97); } }
@keyframes gt-gl-blink { 0%, 100% { opacity: .3; } 50% { opacity: 1; } }
@keyframes gt-gl-crawl { to { background-position: 22px 0, 0 0; } }
@keyframes gt-gl-drop { from { transform: translateY(calc(var(--y, 0px) - 12px)); } }
@keyframes gt-gl-rise { from { transform: translateY(calc(var(--y, 0px) + 9px)); } }
@keyframes gt-gl-slide { from { transform: translateX(-14px); } }
@keyframes gt-gl-pop { from { scale: .35; } }
@keyframes gt-gl-flip { from { rotate: y 90deg; } }
@keyframes gt-gl-flicker { from { scale: 1 .94; } to { scale: 1.04 1.06; } }
@keyframes gt-gl-bob { from { margin-top: -4px; } to { margin-top: 4px; } }
@keyframes gt-gl-bounce { from { margin-top: -9px; } to { margin-top: 5px; } }
@keyframes gt-gl-chomp { from { background: conic-gradient(from 60deg, transparent 0 60deg, var(--b) 60deg); } to { background: conic-gradient(from 88deg, transparent 0 4deg, var(--b) 4deg); } }
@keyframes gt-gl-float { from { transform: translate(0, 0); opacity: .9; } to { transform: translate(18px, -64vh); opacity: 0; } }
@keyframes gt-gl-fall { to { transform: translateY(110vh); } }
@keyframes gt-gl-sway { 0% { transform: translate(0, 0) rotate(0); } 50% { transform: translate(34px, 52vh) rotate(160deg); } 100% { transform: translate(-10px, 108vh) rotate(320deg); } }
@keyframes gt-gl-drift { from { transform: translate(-20px, 6px); } to { transform: translate(60px, -22px); } }
@keyframes gt-gl-grid { to { background-position: 0 24px, 0 0; } }
@keyframes gt-gl-tunnel { from { transform: scale(1); } to { transform: scale(1.5); } }
@keyframes gt-gl-breathe { 0%, 100% { opacity: .7; } 50% { opacity: 1; } }
@media (prefers-reduced-motion: reduce) { .gt-gl *, .gt-gl *::before, .gt-gl *::after { animation: none !important; } }
`;

/* Iron Talon's house style, scoped under .gt-itl (the game's own palette: steel #0b0d13,
   gold #ffd23f, hot #ff4b2b, cyan #4fe6ff, Arial Black titles). */
const TALON_CSS = `
.gt-itl { background: #07080d; color: #eef2f6; font-family: "Segoe UI", Arial, sans-serif; }
.gt-itl-bg { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; transform: scale(1.08);
  filter: blur(3px) saturate(.8) brightness(.32); animation: gt-itl-push 14s ease-out both; }
.gt-itl-veil { position: absolute; inset: 0; background:
  radial-gradient(ellipse at 50% 45%, rgba(40,30,8,.35), rgba(4,5,9,.92) 72%); }
.gt-itl-scan { position: absolute; inset: 0; pointer-events: none; opacity: .18;
  background: repeating-linear-gradient(0deg, rgba(255,255,255,.07) 0 1px, transparent 1px 3px); }
.gt-itl-hazard { position: absolute; left: 0; right: 0; height: 10px; opacity: .9;
  background: repeating-linear-gradient(135deg, #ff4b2b 0 14px, #1a0b08 14px 28px); background-size: 40px 10px;
  animation: gt-itl-crawl 1.2s linear infinite; }
.gt-itl-hazard.top { top: 0; } .gt-itl-hazard.bottom { bottom: 0; animation-direction: reverse; }
.gt-itl-center { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 14px; padding: 24px; text-align: center; }
.gt-itl-kick { margin: 0; font-size: 11px; font-weight: 800; letter-spacing: .38em; text-transform: uppercase; color: #4fe6ff;
  text-shadow: 0 0 12px rgba(79,230,255,.45); }
.gt-itl-title { margin: 0; font-family: "Arial Black", Impact, sans-serif; font-weight: 900; text-transform: uppercase;
  font-size: clamp(34px, 7.5vw, 76px); line-height: .95; letter-spacing: .06em; color: #ffd23f;
  text-shadow: 0 0 28px rgba(255,140,40,.45), 4px 4px 0 #7a1d06; animation: gt-itl-in .5s ease-out both; }
.gt-itl-bar { position: relative; width: min(440px, 82%); height: 14px; margin-top: 8px; background: #0b0d13;
  border: 2px solid #2b2f3a; clip-path: polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px); }
.gt-itl-bar i { position: absolute; left: 0; top: 0; bottom: 0; transition: width .1s linear;
  background: repeating-linear-gradient(135deg, rgba(255,255,255,.18) 0 8px, transparent 8px 16px), linear-gradient(90deg, #ffd23f, #ff8a3a 55%, #ff4b2b);
  background-size: 22px 100%, 100% 100%; animation: gt-itl-crawl .7s linear infinite; box-shadow: 0 0 16px rgba(255,138,58,.55); }
.gt-itl-meta { display: flex; justify-content: space-between; gap: 16px; width: min(440px, 82%);
  font: 700 12px Consolas, "Courier New", ui-monospace, monospace; letter-spacing: .14em; text-transform: uppercase; color: #aab3c2; }
.gt-itl-pct { color: #ffd23f; font-weight: 900; }
.gt-itl-dots em { font-style: normal; animation: gt-itl-blink 1.2s infinite; } .gt-itl-dots em:nth-child(2) { animation-delay: .2s; } .gt-itl-dots em:nth-child(3) { animation-delay: .4s; }
.gt-itl-tip { margin: 18px 0 0; max-width: min(520px, 90%); font-size: 13px; color: #d7dde8; animation: gt-itl-tip .45s ease-out both; }
.gt-itl-tip span { margin-right: 10px; padding: 3px 7px; font-size: 10px; font-weight: 900; letter-spacing: .2em; text-transform: uppercase;
  color: #07080d; background: #ffd23f; }
@keyframes gt-itl-crawl { to { background-position: 40px 0, 0 0; } }
@keyframes gt-itl-push { to { transform: scale(1.0); } }
@keyframes gt-itl-in { from { transform: translateY(10px) scale(.96); } }   /* never from opacity 0: the title must show even if the animation does not run */
@keyframes gt-itl-blink { 0%, 100% { opacity: .2; } 50% { opacity: 1; } }
@keyframes gt-itl-tip { from { opacity: 0; transform: translateY(6px); } }
@media (prefers-reduced-motion: reduce) { .gt-itl *, .gt-itl-bg { animation: none !important; } }
`;

/* Brickfront's house style, scoped under .gt-bfl: a kiln at night. Fired clay #b5482a,
   pale brick #d9805a, mortar #cdbfa8, ember #ffb347, soot #140a07; a slab serif, no
   steel and no hazard stripes. The wall is the progress bar: three courses in running
   bond (the middle one shifted half a brick), stacked bottom-up with column-reverse. */
const BRICK_CSS = `
.gt-bfl { background: #140a07; color: #f3e6d6; font-family: Georgia, "Times New Roman", serif; }
.gt-bfl-bg { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; transform: scale(1.06);
  filter: blur(4px) sepia(.55) saturate(1.1) brightness(.3); animation: gt-bfl-settle 16s ease-out both; }
.gt-bfl-veil { position: absolute; inset: 0; background:
  radial-gradient(ellipse at 50% 38%, rgba(60,22,8,.25), rgba(14,6,4,.94) 74%); }
.gt-bfl-glow { position: absolute; left: 0; right: 0; bottom: 0; height: 46%;
  background: radial-gradient(ellipse at 50% 118%, rgba(255,122,40,.5), rgba(190,60,20,.16) 46%, transparent 72%);
  animation: gt-bfl-breathe 3.6s ease-in-out infinite; }
.gt-bfl-embers { position: absolute; inset: 0; overflow: hidden; }
.gt-bfl-embers i { position: absolute; bottom: -6px; width: 3px; height: 3px; border-radius: 50%; background: #ffb347;
  box-shadow: 0 0 6px 1px rgba(255,150,60,.75); animation: gt-bfl-rise 5.5s linear infinite; }
.gt-bfl-embers i:nth-child(1) { left: 12%; animation-delay: -.4s; } .gt-bfl-embers i:nth-child(2) { left: 29%; animation-delay: -2.9s; animation-duration: 6.6s; }
.gt-bfl-embers i:nth-child(3) { left: 46%; animation-delay: -1.6s; animation-duration: 4.8s; } .gt-bfl-embers i:nth-child(4) { left: 61%; animation-delay: -4.1s; }
.gt-bfl-embers i:nth-child(5) { left: 77%; animation-delay: -3.3s; animation-duration: 7.2s; } .gt-bfl-embers i:nth-child(6) { left: 90%; animation-delay: -1.1s; animation-duration: 5s; }
.gt-bfl-center { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 14px; padding: 24px; text-align: center; }
.gt-bfl-kick { margin: 0; font: 700 11px "Courier New", ui-monospace, monospace; letter-spacing: .34em; text-transform: uppercase; color: #cdbfa8; }
.gt-bfl-title { margin: 0; font-family: Rockwell, "Roboto Slab", "Courier New", Georgia, serif; font-weight: 900; text-transform: uppercase;
  font-size: clamp(32px, 7vw, 72px); line-height: .98; letter-spacing: .08em; color: #e8905f;
  text-shadow: 0 2px 0 #7c2c14, 0 5px 0 #3b140a, 0 0 30px rgba(255,120,40,.35); animation: gt-bfl-set .5s ease-out both; }
.gt-bfl-wall { display: flex; flex-direction: column-reverse; gap: 3px; width: min(440px, 82%); margin-top: 8px; padding: 4px;
  background: #2a1a12; border: 2px solid #4a3324; border-radius: 2px; overflow: hidden; box-shadow: 0 10px 28px rgba(0,0,0,.5); }
.gt-bfl-course { display: flex; gap: 3px; }
.gt-bfl-course.off { margin: 0 calc(-4.17% - 2px); }
.gt-bfl-course i { flex: 1 1 0; height: 11px; border-radius: 1px; background: rgba(205,191,168,.08); box-shadow: inset 0 0 0 1px rgba(205,191,168,.1); }
.gt-bfl-course i.laid { background: linear-gradient(180deg, #d9805a, #b5482a 62%, #8a3219);
  box-shadow: inset 0 1px 0 rgba(255,220,190,.35), inset 0 -1px 0 rgba(0,0,0,.35); animation: gt-bfl-lay .22s ease-out both; }
.gt-bfl-course i.laid:nth-child(3n) { background: linear-gradient(180deg, #c96b45, #a13d22 62%, #7a2a15); }
.gt-bfl-course i.laid:nth-child(5n) { background: linear-gradient(180deg, #e39468, #c2562f 62%, #96391c); }
.gt-bfl-course i.next { background: rgba(255,179,71,.28); box-shadow: 0 0 10px rgba(255,150,60,.6); animation: gt-bfl-hot 1s ease-in-out infinite; }
.gt-bfl-meta { display: flex; justify-content: space-between; gap: 16px; width: min(440px, 82%);
  font: 700 12px "Courier New", ui-monospace, monospace; letter-spacing: .12em; text-transform: uppercase; color: #cdbfa8; }
.gt-bfl-pct { color: #ffb347; font-weight: 900; }
.gt-bfl-dots em { font-style: normal; animation: gt-bfl-hot 1.2s infinite; } .gt-bfl-dots em:nth-child(2) { animation-delay: .2s; } .gt-bfl-dots em:nth-child(3) { animation-delay: .4s; }
.gt-bfl-tip { margin: 18px 0 0; max-width: min(520px, 90%); font-size: 14px; font-style: italic; color: #ecdcc8; animation: gt-bfl-tip .45s ease-out both; }
.gt-bfl-tip span { margin-right: 10px; padding: 3px 8px; font: 900 10px "Courier New", ui-monospace, monospace; font-style: normal; letter-spacing: .18em;
  text-transform: uppercase; color: #140a07; background: #cdbfa8; border-bottom: 2px solid #b5482a; }
@keyframes gt-bfl-settle { to { transform: scale(1.0); } }
@keyframes gt-bfl-set { from { transform: translateY(-14px); } }   /* a transform only, never opacity 0: the title must show even if the animation does not run */
@keyframes gt-bfl-lay { from { transform: translateY(-9px); } }     /* likewise: a laid brick is there whether or not it drops */
@keyframes gt-bfl-hot { 0%, 100% { opacity: .35; } 50% { opacity: 1; } }
@keyframes gt-bfl-breathe { 0%, 100% { opacity: .7; } 50% { opacity: 1; } }
@keyframes gt-bfl-rise { from { transform: translate(0, 0); opacity: .9; } to { transform: translate(18px, -62vh); opacity: 0; } }
@keyframes gt-bfl-tip { from { transform: translateY(6px); } }
@media (prefers-reduced-motion: reduce) { .gt-bfl *, .gt-bfl-bg { animation: none !important; } }
`;
